import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
// Using a compatible date picker alternative
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { base_url } from '../config/config';
import axios from 'axios';

const defaultInvoice = {
  rechnungsNummer: '',
  rechnungsDatum: new Date(),
  faellig: null,
  firmenname: '',
  kundennummer: '',
  kundenID: null,
  nettobetrag: '',
  mehrwertsteuer: '',
  mehrwertsteuersatz: 19,
  gesamtbetrag: '',
  zahlungsbedingungen: 'Zahlbar innerhalb von 14 Tagen',
  bezahltAm: null,
  anmerkungen: '',
  zuordnung1: '',
  zuordnung2: ''
};

const InvoiceForm = ({ invoice, isEdit, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(isEdit && invoice ? { ...invoice } : { ...defaultInvoice });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState({ message: '', severity: 'info' });
  const [isPaid, setIsPaid] = useState(isEdit && invoice?.bezahltAm ? true : false);

  // Fetch customers for the autocomplete
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/kunden/all`);
        if (response.data.status === 1 && response.data.data) {
          setCustomers(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        setAlertMessage({
          message: 'Fehler beim Laden der Kunden',
          severity: 'error'
        });
      }
    };

    fetchCustomers();
  }, []);

  // Set form data from invoice if in edit mode
  useEffect(() => {
    if (isEdit && invoice) {
      setFormData({
        ...invoice,
        // Handle dates specifically
        rechnungsDatum: invoice.rechnungsDatum ? new Date(invoice.rechnungsDatum) : null,
        faellig: invoice.faellig ? new Date(invoice.faellig) : null,
        bezahltAm: invoice.bezahltAm ? new Date(invoice.bezahltAm) : null,
      });
      setIsPaid(!!invoice.bezahltAm);
    } else {
      // Set defaults for new invoice
      const today = new Date();
      setFormData({
        ...defaultInvoice,
        rechnungsDatum: today,
        // Default due date is 14 days from today
        faellig: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14)
      });
    }
  }, [isEdit, invoice]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Recalculate total amounts if necessary
    if (['nettobetrag', 'mehrwertsteuer', 'mehrwertsteuersatz'].includes(name)) {
      calculateTotals(name, value);
    }
  };

  // Handle customer selection from autocomplete
  const handleCustomerChange = (event, newValue) => {
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        kundenID: newValue._id,
        firmenname: newValue.bezeichnung,
        kundennummer: newValue.kundennummer || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        kundenID: null,
        firmenname: '',
        kundennummer: ''
      }));
    }
  };

  // Handle date changes
  const handleDateChange = (name) => (date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  // Handle paid status toggle
  const handlePaidToggle = (e) => {
    const checked = e.target.checked;
    setIsPaid(checked);
    setFormData(prev => ({
      ...prev,
      bezahltAm: checked ? new Date() : null
    }));
  };

  // Calculate total amounts based on inputs
  const calculateTotals = (changedField, value) => {
    setCalculating(true);
    
    // Get current values and the changed value
    let netAmount = changedField === 'nettobetrag' ? parseFloat(value) || 0 : parseFloat(formData.nettobetrag) || 0;
    let vatRate = changedField === 'mehrwertsteuersatz' ? parseFloat(value) || 0 : parseFloat(formData.mehrwertsteuersatz) || 0;
    let vatAmount = changedField === 'mehrwertsteuer' ? parseFloat(value) || 0 : parseFloat(formData.mehrwertsteuer) || 0;
    
    // If net amount or VAT rate changes, recalculate VAT amount
    if (changedField === 'nettobetrag' || changedField === 'mehrwertsteuersatz') {
      vatAmount = (netAmount * vatRate) / 100;
    }
    // If VAT amount changes directly, we don't need to recalculate it
    
    // Calculate total amount
    const totalAmount = netAmount + vatAmount;
    
    // Update form data with calculations
    setFormData(prev => ({
      ...prev,
      nettobetrag: netAmount,
      mehrwertsteuer: vatAmount,
      mehrwertsteuersatz: vatRate,
      gesamtbetrag: totalAmount
    }));
    
    setCalculating(false);
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.rechnungsNummer) {
      newErrors.rechnungsNummer = 'Rechnungsnummer ist erforderlich';
    }
    
    if (!formData.rechnungsDatum) {
      newErrors.rechnungsDatum = 'Rechnungsdatum ist erforderlich';
    }
    
    if (!formData.firmenname && !formData.kundenID) {
      newErrors.firmenname = 'Firmenname oder Kunde ist erforderlich';
    }
    
    if (formData.nettobetrag === '' || isNaN(formData.nettobetrag)) {
      newErrors.nettobetrag = 'Nettobetrag muss eine Zahl sein';
    }
    
    if (formData.mehrwertsteuer === '' || isNaN(formData.mehrwertsteuer)) {
      newErrors.mehrwertsteuer = 'Mehrwertsteuer muss eine Zahl sein';
    }
    
    if (formData.mehrwertsteuersatz === '' || isNaN(formData.mehrwertsteuersatz)) {
      newErrors.mehrwertsteuersatz = 'Mehrwertsteuersatz muss eine Zahl sein';
    }
    
    if (formData.gesamtbetrag === '' || isNaN(formData.gesamtbetrag)) {
      newErrors.gesamtbetrag = 'Gesamtbetrag muss eine Zahl sein';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      setAlertMessage({
        message: 'Bitte korrigieren Sie die markierten Felder',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        // Format dates for API
        rechnungsDatum: formData.rechnungsDatum ? format(new Date(formData.rechnungsDatum), 'yyyy-MM-dd') : null,
        faellig: formData.faellig ? format(new Date(formData.faellig), 'yyyy-MM-dd') : null,
        bezahltAm: formData.bezahltAm ? format(new Date(formData.bezahltAm), 'yyyy-MM-dd') : null,
      };
      
      onSubmit(submissionData);
    } catch (error) {
      console.error('Error preparing submission:', error);
      setAlertMessage({
        message: 'Fehler bei der Verarbeitung des Formulars',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {isEdit ? 'Rechnung bearbeiten' : 'Neue Rechnung erstellen'}
      </DialogTitle>
      <DialogContent dividers>
        {alertMessage.message && (
          <Alert severity={alertMessage.severity} sx={{ mb: 2 }}>
            {alertMessage.message}
          </Alert>
        )}
        
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {/* Main invoice information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Rechnungsinformationen
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rechnungsnummer"
                name="rechnungsNummer"
                value={formData.rechnungsNummer || ''}
                onChange={handleChange}
                error={!!errors.rechnungsNummer}
                helperText={errors.rechnungsNummer}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Rechnungsdatum
                </Typography>
                <DatePicker
                  selected={formData.rechnungsDatum}
                  onChange={(date) => handleDateChange('rechnungsDatum')(date)}
                  dateFormat="dd.MM.yyyy"
                  className={`date-picker-custom ${errors.rechnungsDatum ? 'date-picker-error' : ''}`}
                  customInput={
                    <TextField 
                      fullWidth
                      error={!!errors.rechnungsDatum}
                      helperText={errors.rechnungsDatum}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  }
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Fälligkeitsdatum
                </Typography>
                <DatePicker
                  selected={formData.faellig}
                  onChange={(date) => handleDateChange('faellig')(date)}
                  dateFormat="dd.MM.yyyy"
                  className={`date-picker-custom ${errors.faellig ? 'date-picker-error' : ''}`}
                  customInput={
                    <TextField 
                      fullWidth
                      error={!!errors.faellig}
                      helperText={errors.faellig}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  }
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zahlungsbedingungen"
                name="zahlungsbedingungen"
                value={formData.zahlungsbedingungen || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            {/* Customer information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Kundeninformationen
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                id="customer-autocomplete"
                options={customers || []}
                getOptionLabel={(option) => `${option.bezeichnung}${option.kundennummer ? ` (${option.kundennummer})` : ''}`}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={customers.find(c => c._id === formData.kundenID) || null}
                onChange={handleCustomerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kunde auswählen"
                    error={!!errors.firmenname}
                    helperText={errors.firmenname}
                    fullWidth
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Firmenname"
                name="firmenname"
                value={formData.firmenname || ''}
                onChange={handleChange}
                disabled={!!formData.kundenID}
                error={!!errors.firmenname}
                helperText={errors.firmenname || 'Wird automatisch aus Kunde übernommen'}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kundennummer"
                name="kundennummer"
                value={formData.kundennummer || ''}
                onChange={handleChange}
                disabled={!!formData.kundenID}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            {/* Financial information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Finanzielle Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nettobetrag"
                name="nettobetrag"
                type="number"
                value={formData.nettobetrag}
                onChange={handleChange}
                error={!!errors.nettobetrag}
                helperText={errors.nettobetrag}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mehrwertsteuersatz"
                name="mehrwertsteuersatz"
                type="number"
                value={formData.mehrwertsteuersatz}
                onChange={handleChange}
                error={!!errors.mehrwertsteuersatz}
                helperText={errors.mehrwertsteuersatz}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      %
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mehrwertsteuer"
                name="mehrwertsteuer"
                type="number"
                value={formData.mehrwertsteuer}
                onChange={handleChange}
                error={!!errors.mehrwertsteuer}
                helperText={errors.mehrwertsteuer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Gesamtbetrag"
                name="gesamtbetrag"
                type="number"
                value={formData.gesamtbetrag}
                onChange={handleChange}
                error={!!errors.gesamtbetrag}
                helperText={errors.gesamtbetrag}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon />
                    </InputAdornment>
                  ),
                  endAdornment: calculating && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPaid}
                    onChange={handlePaidToggle}
                    color="success"
                  />
                }
                label="Rechnung ist bezahlt"
              />
              
              {isPaid && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Bezahlt am
                  </Typography>
                  <DatePicker
                    selected={formData.bezahltAm}
                    onChange={(date) => handleDateChange('bezahltAm')(date)}
                    dateFormat="dd.MM.yyyy"
                    className="date-picker-custom"
                    customInput={
                      <TextField 
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CalendarIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                      />
                    }
                  />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            {/* Additional information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Zusätzliche Informationen
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zuordnung 1"
                name="zuordnung1"
                value={formData.zuordnung1 || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zuordnung 2"
                name="zuordnung2"
                value={formData.zuordnung2 || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Anmerkungen"
                name="anmerkungen"
                value={formData.anmerkungen || ''}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          startIcon={<CancelIcon />}
        >
          Abbrechen
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isEdit ? 'Aktualisieren' : 'Erstellen'}
        </Button>
      </DialogActions>
    </>
  );
};

export default InvoiceForm;